import { createContext, ReactNode, useContext, useState } from 'react';

export interface ModalConfig {
  id: string;
  heading: string;
  component: ReactNode;
}

export interface ModalContextType {
  openModal: ({
    component,
    heading,
  }: {
    component: ReactNode;
    heading: string;
  }) => string;
  closeModal: (id: string) => void;
  closeAllModals: () => void;
  modals: ModalConfig[];
  isAnyModalOpen: boolean;
}

export const ModalContext = createContext<ModalContextType | null>(null);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modals, setModals] = useState<ModalConfig[]>([]);
  const openModal = ({
    component,
    heading,
  }: {
    component: ReactNode;
    heading: string;
  }): string => {
    const id = `modal-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setModals((previousModals) => [
      ...previousModals,
      { id, component, heading },
    ]);
    return id;
  };

  const closeModal = (id: string): void => {
    setModals((previousModals) =>
      previousModals.filter((modal) => modal.id !== id)
    );
  };

  const closeAllModals = (): void => {
    setModals([]);
  };

  const contextValue = {
    closeModal,
    openModal,
    closeAllModals,
    modals,
    isAnyModalOpen: modals.length > 0,
  };

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModal(): ModalContextType {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error(
      'useModal must be used with a ModalProvider. Make sure your component is wrapped with <ModalProvider>...</ModalProvider>'
    );
  }
  return context;
}
